import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Box, MenuList, Stack, Typography, useMediaQuery } from "@mui/material";

import { ERoutes } from "@utils/constants";
import { bindToggle } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { usePendingEmailReminder } from "@/contexts/PendingEmailReminderContext";
import { useAuth, useLogOut } from "@/hooks/auth";
import { useActionRequiredCount } from "@/hooks/useActionRequiredCount";
import { useAppSelector } from "@/store/hooks";
import { getFavoritesState } from "@/store/selectors/getFavorites";
import { colors } from "@/styles/design-system";
import { handleCloseOnKeyDown, Menu } from "@/ui-kit";

// Plan tier pill colors / labels — same source of truth as LoggedInBanner.
const PLAN_LABELS: Record<AccountType, string> = {
  [AccountType.FREE]: "Free Plan",
  [AccountType.SUBSCRIPTION]: "Subscription",
  [AccountType.MUSIC_CREATOR]: "Music Creator",
};
/** The Subscription pill's background in THIS menu only. Deliberately not in
    `colors.tier`: putting it there would apply it to every surface that reads
    the tier tint, and the ask was for this one pill. */
const SUBSCRIPTION_PILL_GRADIENT =
  "linear-gradient(100deg, rgb(80 187 215) 0%, rgb(115 170 227) 55%, rgb(24 149 205) 100%)";

const PLAN_TINTS: Record<AccountType, { bg: string; text: string }> = {
  [AccountType.FREE]: { bg: colors.tier.free.bg, text: colors.tier.free.text },
  [AccountType.SUBSCRIPTION]: { bg: colors.tier.subscription.bg, text: colors.tier.subscription.text },
  [AccountType.MUSIC_CREATOR]: { bg: "rgba(156, 211, 253, 0.18)", text: colors.link.blue },
};
// While whitelisting is paused the pill stops naming the plan and says what is
// actually true — "Subscription" is misleading once services are frozen, and
// plainly wrong once the paid period has lapsed. Each state carries the colour
// of the banner that explains it: red while a credit is missing, amber once it
// is restored and the cycle has run out. Decision 2026-07-25, user.
// Solid, and the exact banner colours — a tinted version read as a washed-out
// plan badge rather than a state. White text on both.
export const PAUSED_TINTS: Record<"credit_missing" | "awaiting", { bg: string; text: string }> = {
  credit_missing: { bg: "rgb(195, 75, 54)", text: "#fff" },
  awaiting: { bg: "rgb(191, 117, 22)", text: "#fff" },
};
const I = ({ d, style }: { d: string; style?: React.CSSProperties }) => (
  <svg
    viewBox="0 0 24 24"
    style={{
      width: "16px",
      height: "16px",
      fill: "none",
      stroke: "#8B7460",
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      flexShrink: 0,
      ...style,
    }}
  >
    <path d={d} />
  </svg>
);

// Multi-path icon
const IM = ({ paths, style }: { paths: string[]; style?: React.CSSProperties }) => (
  <svg
    viewBox="0 0 24 24"
    style={{
      width: "16px",
      height: "16px",
      fill: "none",
      stroke: "#8B7460",
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      flexShrink: 0,
      ...style,
    }}
  >
    {paths.map((d, i) => (
      <path key={i} d={d} />
    ))}
  </svg>
);

// Exact link style from demo: .acct-a-link
const linkSx = {
  fontFamily: "Raleway",
  fontSize: "13px",
  fontWeight: 400,
  color: "#5C3D28",
  textDecoration: "none",
  padding: "8px 10px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(89, 65, 46, 0.08)",
    color: "#3a2616",
    "& svg": { stroke: "#3a2616" },
  },
};

const MenuLink: React.FC<{ to: string; onClick?: (e: React.MouseEvent) => void; sx?: any; children: React.ReactNode }> = ({ to, onClick, sx, children }) => (
  <Box
    component={Link}
    to={to}
    sx={{ ...linkSx, ...sx }}
    onClick={(e: React.MouseEvent) => {
      if (onClick) {
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          onClick(e);
        }
      }
    }}
  >
    {children}
  </Box>
);

const AccountMenu = () => {
  const { t } = useTranslation("navigation");
  const { myData } = useAuth();
  const favoritesState = useAppSelector(getFavoritesState);
  const popupState = usePopupState({ variant: "popper", popupId: "account-menu-popover" });
  const { logoutUser, resetUser } = useLogOut();
  const navigate = useNavigate();
  const { accountType, pauseState } = useAccountType();
  const isPaused = pauseState !== "none";
  const actionRequiredCount = useActionRequiredCount();
  // Pending email verification surfaces as a red badge in two places:
  // (1) a dot on the avatar trigger so the user notices even when the
  // dropdown is closed, (2) a count badge next to the "Account" row
  // inside the dropdown so they know where to go. Both are gated on
  // `shouldShowReminder` from the reminder context, which only flips
  // true AFTER the user has closed the verify modal at least once —
  // otherwise we'd nag them while the modal is the active task.
  const { shouldShowReminder: hasPendingEmail } = usePendingEmailReminder();

  // MOBILE ONLY (<1150, the SecondRootLayout header-swap boundary): nudge the
  // dropdown right, because on the mobile bar the avatar is not the last item —
  // the ☰ button sits to its right, so a plain bottom-end alignment leaves the
  // menu visibly left of where it belongs. Positive skidding moves it toward the
  // screen edge; Popper's preventOverflow still clamps it, so it cannot clip.
  // Desktop keeps [0, 27] exactly as before.
  const isMobile = useMediaQuery("(max-width:1149px)");
  const menuOffset: [number, number] = isMobile ? [40, 27] : [0, 27];

  const handleNavigate = (route: ERoutes) => {
    popupState.close();
    setTimeout(() => navigate(route), 500);
  };

  return (
    <>
      {/* Avatar button — exact demo: 40px, 1.5px white outline, Raleway 15px 600 */}
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <Box
          component="button"
          // bindToggle, not bindTrigger: tapping the avatar while the menu is
          // open closes it again. The ClickAwayListener inside Menu also fires
          // on this tap, but React's delegated handler runs first (close), so
          // the click-away that follows is a no-op rather than a reopen.
          {...bindToggle(popupState)}
          sx={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "1.5px solid rgba(255,255,255,0.5)",
            backgroundColor: "transparent",
            color: "rgba(255,255,255,0.8)",
            fontFamily: "Raleway, sans-serif",
            fontSize: "15px",
            fontWeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            userSelect: "none",
            padding: 0,
            outline: "none",
            // Only apply hover styling on hover-capable (desktop) devices — on
            // touch, :hover sticks after a tap and left the icon looking
            // permanently "active" whether the menu was open or closed.
            "@media (hover: hover)": {
              "&:hover": {
                borderColor: "rgba(255,255,255,0.5)",
                backgroundColor: "rgba(255,255,255,0.12)",
                color: "#fff",
              },
            },
          }}
        >
          {myData?.firstName[0].toUpperCase()}
        </Box>
        {/* Pending-claims badge intentionally removed from the avatar —
            the same count now lives on the "Claims" main-nav item, so
            duplicating it here is redundant. The dropdown menu keeps its
            own badge next to the Claims row. */}
        {/* Pending email verification: red circle "1" overlaid on the
            top-right of the avatar. Pure CSS pin (no popup state, no
            click handler) so it doesn't block the avatar button below. */}
        {hasPendingEmail && (
          // Pending-email count — matches the favorites/cart/claims
          // badges in SecondHeader exactly (top/right offsets, size,
          // padding, font, weight). Only the color differs by design.
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: "-3px",
              right: "-6px",
              minWidth: "18px",
              height: "18px",
              padding: "0 5px",
              borderRadius: "9px",
              background: "rgb(209, 116, 50)",
              color: "#fff",
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontSize: "10px",
              fontWeight: 600,
              lineHeight: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <span style={{ display: "inline-block", transform: "translateY(-1px)" }}>1</span>
          </Box>
        )}
      </Box>

      {/* Dropdown — exact demo: #EDE0D1, 260px, 12px radius, 20px padding */}
      <Menu
        popupState={popupState}
        offset={menuOffset}
        paperProps={{
          elevation: 0,
          width: "260px",
          sx: {
            width: "260px",
            overflow: "visible",
            filter: "drop-shadow(0px 16px 48px rgba(0,0,0,0.25))",
            border: "none",
            borderRadius: "12px",
            backgroundClip: "unset",
            backgroundImage: "none",
            backgroundColor: "#fffbf7",
            // MOBILE ONLY (<1150, the SecondRootLayout header-swap boundary):
            // this same dropdown is the account menu on phone/tablet too, where
            // the full item list can be taller than the screen. Cap it to the
            // viewport minus the header bar and let it scroll. dvh so iOS
            // Safari's collapsing toolbar doesn't clip the last row. Desktop is
            // deliberately untouched — it keeps overflow:visible and never scrolls.
            "@media (max-width: 1149px)": {
              maxHeight: "calc(100dvh - 96px)",
              overflowY: "auto",
              overscrollBehavior: "contain",
            },
          },
        }}
        placement="bottom-end"
      >
        <MenuList
          autoFocus={popupState.isOpen}
          sx={{ padding: 0 }}
          onKeyDown={(event) => handleCloseOnKeyDown(event, popupState)}
        >
          <Stack sx={{ padding: "20px" }}>
            {/* Header: avatar + name/email — exact demo .acct-a-header */}
            <Stack direction="row" alignItems="center" gap="14px" pb="4px">
              <Box
                sx={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  backgroundColor: "#59412E",
                  color: "#FBF4EC",
                  fontFamily: "Raleway, sans-serif",
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "44px",
                  textAlign: "center",
                  flexShrink: 0,
                }}
              >
                {myData?.firstName[0].toUpperCase()}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#3a2616",
                    lineHeight: 1,
                  }}
                >
                  {myData?.firstName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "11px",
                    fontWeight: 400,
                    color: "#8B7460",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {myData?.email}
                </Typography>
              </Box>
            </Stack>

            {/* Plan status */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                background: "rgba(89, 65, 46, 0.05)",
                border: "1px solid rgba(89, 65, 46, 0.08)",
                borderRadius: "10px",
                padding: "12px 14px",
                margin: "10px 0 0 0",
                textDecoration: "none",
                cursor: "default",
              }}
            >
              {/* Top row: plan name + action link */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box
                  component="span"
                  sx={{
                    fontFamily: "Raleway, sans-serif",
                    fontSize: accountType === AccountType.MUSIC_CREATOR ? "10.5px" : "13px",
                    fontWeight: accountType === AccountType.MUSIC_CREATOR ? 500 : 400,
                    lineHeight: 1,
                    color: isPaused ? PAUSED_TINTS[pauseState as "credit_missing" | "awaiting"].text
                      : accountType === AccountType.FREE ? "#fff" : PLAN_TINTS[accountType].text,
                    background: isPaused ? PAUSED_TINTS[pauseState as "credit_missing" | "awaiting"].bg
                      : accountType === AccountType.FREE ? "#20bd84"
                      // Subscription gets a gradient here and ONLY here — this
                      // menu is where the plan is stated rather than merely
                      // referenced, so it is worth the weight. The flat tint
                      // stays everywhere else, LoggedInBanner included.
                      : accountType === AccountType.SUBSCRIPTION ? SUBSCRIPTION_PILL_GRADIENT
                      : PLAN_TINTS[accountType].bg,
                    borderRadius: "20px",
                    padding: "7px 10px",
                    whiteSpace: "nowrap",
                    letterSpacing: "0.3px",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {isPaused ? "Paused" : PLAN_LABELS[accountType]}
                </Box>
                {accountType !== AccountType.MUSIC_CREATOR && (
                  <Typography
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      popupState.close();
                      // Both Free and Subscription users navigate to the
                      // subscription page — the page itself surfaces the
                      // appropriate CTAs (Subscribe / Manage Subscription).
                      // Do NOT auto-open the manage modal or cart slide-out;
                      // the user should land on the page first.
                      navigate(ERoutes.AccountSubscription);
                    }}
                    sx={{
                      fontFamily: "Raleway, sans-serif",
                      fontSize: "11px",
                      fontWeight: 400,
                      color: "#4a90c4",
                      lineHeight: 1,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {accountType === AccountType.FREE ? "Upgrade Now" : "Manage"}
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Divider */}
            <Box sx={{ height: "1px", background: "rgba(89, 65, 46, 0.12)", margin: "14px 0" }} />

            {/* Nav links — exact demo .acct-a-nav with gap 4px */}
            <Stack gap="4px">
              <MenuLink to={ERoutes.AccountVideos} onClick={() => handleNavigate(ERoutes.AccountVideos)}>
                <IM paths={[
                  "M23 7l-7 5 7 5V7z",
                  "M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z",
                ]} />
                Claims
                {actionRequiredCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "18px",
                      height: "18px",
                      padding: "0 5px",
                      borderRadius: "9px",
                      background: "rgba(156, 211, 253, 0.2)",
                      color: "#4a90c4",
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                      fontSize: "10px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      marginLeft: 0,
                      letterSpacing: "0.3px",
                      transform: "translateY(-1px)",
                    }}
                  >
                    {actionRequiredCount}
                  </Box>
                )}
              </MenuLink>

              <MenuLink to={ERoutes.Favorites} onClick={() => handleNavigate(ERoutes.Favorites)}>
                <I d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                {t("favorites")}
                {favoritesState.length > 0 && (
                  // Dropdown badge — circle by default (18×18 minWidth=height,
                  // padding 0 5px) so single digits look round; stretches
                  // horizontally for "99+" via flex. Matches the four
                  // website-header badges' geometry exactly.
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "18px",
                      height: "18px",
                      padding: "0 5px",
                      borderRadius: "9px",
                      background: "rgba(89, 65, 46, 0.12)",
                      color: "#5C3D28",
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                      fontSize: "10px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      marginLeft: 0,
                      letterSpacing: "0.3px",
                      transform: "translateY(-1px)",
                    }}
                  >
                    {favoritesState.length > 99 ? "99+" : favoritesState.length}
                  </Box>
                )}
              </MenuLink>

              <MenuLink to={ERoutes.AccountOrders} onClick={() => handleNavigate(ERoutes.AccountOrders)}>
                <IM paths={[
                  "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
                  "M14 2v6h6",
                  "M16 13H8",
                  "M16 17H8",
                ]} />
                Orders
              </MenuLink>

              <Box sx={{ height: "1px", background: "rgba(89, 65, 46, 0.12)", margin: "10px 0" }} />

              <MenuLink to={ERoutes.Deals} onClick={() => handleNavigate(ERoutes.Deals)}>
                <IM paths={[
                  "M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z",
                  "M7 7h.01",
                ]} />
                {t("bestDeals")}
              </MenuLink>

              <MenuLink to={ERoutes.Newsletter} onClick={() => handleNavigate(ERoutes.Newsletter)}>
                <IM
                  paths={[
                    "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z",
                    "M22 6l-10 7L2 6",
                  ]}
                  style={{ transform: "translateY(-1px)" }}
                />
                {t("newsletter")}
              </MenuLink>

              <MenuLink to={ERoutes.DownloadInstruction} onClick={() => handleNavigate(ERoutes.DownloadInstruction)}>
                <IM paths={[
                  "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4",
                  "M7 10l5 5 5-5",
                  "M12 15V3",
                ]} />
                {t("downloadInstructions")}
              </MenuLink>

              <MenuLink to={ERoutes.MUSIC_CREATOR_PROGRAM} onClick={() => handleNavigate(ERoutes.MUSIC_CREATOR_PROGRAM)}>
                <I d="M5 3l14 9-14 9V3z" style={{ transform: "translateX(1px)" }} />
                {t("ytCreatorProgram")}
              </MenuLink>
            </Stack>

            {/* Divider */}
            <Box sx={{ height: "1px", background: "rgba(89, 65, 46, 0.12)", margin: "14px 0" }} />

            {/* Settings + Logout — exact demo */}
            <Stack gap="4px">
              <MenuLink to={ERoutes.Profile} onClick={() => handleNavigate(ERoutes.Profile)}>
                <IM paths={[
                  "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2",
                  "M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
                ]} />
                Account
                {hasPendingEmail && (
                  // Red action-required badge mirroring the Claims badge
                  // shape (above) so the two visual treatments stay
                  // consistent — colored red instead of blue because
                  // pending email verification is a higher-urgency state
                  // than claims-pending. -2px translateY nudges the pill
                  // up so its visual center aligns with the row icon /
                  // text baseline instead of dropping below it.
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "18px",
                      height: "18px",
                      padding: "0 5px",
                      borderRadius: "9px",
                      background: "rgb(209, 116, 50)",
                      color: "#fff",
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                      fontSize: "10px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      marginLeft: 0,
                      letterSpacing: "0.3px",
                      transform: "translateY(-1px)",
                    }}
                  >
                    1
                  </Box>
                )}
              </MenuLink>

              <Box
                sx={linkSx}
                onClick={() => {
                  logoutUser();
                  resetUser();
                  handleNavigate(ERoutes.Home);
                }}
              >
                <IM
                  paths={[
                    "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4",
                    "M16 17l5-5-5-5",
                    "M21 12H9",
                  ]}
                  style={{ transform: "translateX(1px)" }}
                />
                {t("logOut")}
              </Box>
            </Stack>
          </Stack>
        </MenuList>
      </Menu>
    </>
  );
};

export default AccountMenu;
